import { useAppSelector } from '@app/core/hooks/redux-hooks';
import { InstrumentDefinitionWithId } from '@app/core/store/capital-structure-slice-selectors';
import {
  getWaterfallCaseInstrumentValue,
  getWaterfallCaseYearInstrumentTransactionSummary,
  getWaterfallInstrumentValue,
} from '@app/core/store/pwerm2-calculation-slice-selectors';
import CellValue from '@app/shared/components/cell-value/CellValue';
import { amountRules } from '@app/shared/components/cell-value/CellValueConfigurations';
import { l } from '@locale/setupI18n';
import { FC } from 'react';
import TableSectionValuesPerForecast from '../../components/TableSectionValuesPerForecast';
import { InstrumentTransactions } from './utils/transactionUtils';
import {
  getMvLabel,
  getWeightedAverageMvLabel,
  ValuationSection,
} from './utils/valuationLabelHelper';
import TableSectionValuesPerCase from '../../components/TableSectionValuesPerCase';
import { Alignment } from '@app/shared/enums/alignment.enum';
import { InstrumentInfusionCashFlows } from './InstrumentInfusionCashFlows';
import { InstrumentOtherInterimCashFlows } from './InstrumentOtherInterimCashFlows';

export const InstrumentInterimCashflows: FC<{
  instrument: InstrumentDefinitionWithId;
  columnsCount: number;
  instrumentTransactions: InstrumentTransactions;
}> = ({ instrument, columnsCount, instrumentTransactions }) => {
  const results = useAppSelector((state) => state.pwerm2Calculation.calculatedResults);

  const {
    infusionTransactions,
    redemptionTransactions,
    cashPaidTransactions,
    dividendTransactions,
    containsInfusions: instrumentContainsInfusions,
    containsRedemptions: instrumentContainsRedemptions,
    containsCashPaid: instrumentContainsCashPaid,
    containsDividends: instrumentContainsDividends,
    hasInterims: shouldDisplayInterimCashflow,
  } = instrumentTransactions;

  const instrumentContainsOtherCashFlowMovement =
    instrumentContainsDividends || instrumentContainsCashPaid || instrumentContainsRedemptions;

  if (!shouldDisplayInterimCashflow) {
    return null;
  }

  return (
    <>
      {instrumentContainsInfusions && (
        <InstrumentInfusionCashFlows
          instrument={instrument}
          infusionTransactions={infusionTransactions}
          columnsCount={columnsCount}
        />
      )}
      {instrumentContainsOtherCashFlowMovement && (
        <InstrumentOtherInterimCashFlows
          instrument={instrument}
          hasInfusions={instrumentContainsInfusions}
          redemptionTransactions={redemptionTransactions}
          dividendTransactions={dividendTransactions}
          cashInterestTransactions={cashPaidTransactions}
          columnsCount={columnsCount}
        />
      )}

      <TableSectionValuesPerForecast
        title={l('_CumulativePVOfInterimCashFlows')}
        renderSpacer
        additionalTitleClassNames="table-primary__cell--strong"
        renderCell={({ forecast, caseItem }) => (
          <CellValue
            {...amountRules}
            className="table-primary__cell--strong"
            value={getWaterfallCaseYearInstrumentTransactionSummary(
              results,
              caseItem.caseId,
              forecast.forecastId,
              instrument.instrumentId,
              (transactionSummary) => transactionSummary.cumulativePVOfCashflowMovement,
              true
            )}
            dataTestId={`cumulative-pv-of-cashflow-movement-cell-${caseItem.caseId}-${forecast.forecastId}`}
          />
        )}
      />
      <TableSectionValuesPerCase
        title={getMvLabel({
          section: ValuationSection.InterimCashflows,
          instrument,
        })}
        renderSpacer={false}
        additionalTitleClassNames="table-primary__cell--strong table-primary__cell--background-secondary"
        additionalCellClassNames="table-primary__cell--background-secondary"
        renderCell={({ caseItem }) => (
          <CellValue
            {...amountRules}
            alignment={Alignment.Center}
            className="table-primary__cell--strong"
            value={getWaterfallCaseInstrumentValue(
              results,
              caseItem.caseId,
              instrument.instrumentId,
              (instrument) => instrument.weightedInterimDiscountedFairValue
            )}
            dataTestId={`mv-of-interim-cashflows-cell-${caseItem.caseId}-${instrument.instrumentId}`}
          />
        )}
      />
      <tr>
        <th className="table-primary__cell--vertical-separator table-primary__cell--background-tertiary table-primary__cell--strong">
          {getWeightedAverageMvLabel({
            section: ValuationSection.InterimCashflows,
            instrument,
          })}
        </th>
        <td className="table-primary__cell--vertical-separator table-primary__cell--background-tertiary" />
        <td
          colSpan={columnsCount - 2}
          className="table-primary__cell--background-tertiary table-primary__cell--strong">
          <CellValue
            {...amountRules}
            alignment={Alignment.Center}
            strong
            value={getWaterfallInstrumentValue(
              results,
              instrument.instrumentId,
              (instrument) => instrument.weightedInterimDiscountedFairValue
            )}
            dataTestId={`weighted-average-mv-of-interim-cashflows-cell-${instrument.instrumentId}`}
          />
        </td>
      </tr>
    </>
  );
};
